import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Col, Form, InputGroup, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaBullhorn, FaCalendarAlt, FaSearch } from "react-icons/fa";
import UserService from "../../services/user/UserService";

const TYPE_LABELS = {
    FEATURE: "Fitur Baru",
    UPDATE: "Pembaruan",
    IMPORTANT: "Penting",
    MAINTENANCE: "Pemeliharaan",
};

const TYPE_VARIANTS = {
    FEATURE: "success",
    UPDATE: "primary",
    IMPORTANT: "danger",
    MAINTENANCE: "warning",
};

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "long",
        year: "numeric",
    });
};

const isExpired = (item) => item.endAt && new Date(item.endAt).getTime() < Date.now();

const AnnouncementCenter = () => {
    const service = useMemo(() => new UserService(), []);
    const navigate = useNavigate();
    const { id } = useParams();
    const [items, setItems] = useState([]);
    const [detail, setDetail] = useState(null);
    const [search, setSearch] = useState("");
    const [type, setType] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError("");
            try {
                if (id) {
                    const response = await service.getAnnouncementArchiveDetail(id);
                    if (!cancelled) setDetail(response.data);
                } else {
                    const response = await service.listAnnouncementsArchive({ search, type });
                    if (!cancelled) setItems(Array.isArray(response.data) ? response.data : []);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(
                        err?.response?.status === 404
                            ? "Pengumuman tidak ditemukan atau tidak tersedia untuk akun Anda."
                            : err?.response?.status === 401
                                ? "Sesi login berakhir. Silakan login kembali."
                                : "Pengumuman belum dapat dimuat. Silakan coba kembali."
                    );
                    setDetail(null);
                    setItems([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [service, id, search, type]);

    const sortedItems = useMemo(() => {
        return [...items].sort((a, b) => {
            const priorityDiff = (Number(b.priority) || 0) - (Number(a.priority) || 0);
            if (priorityDiff !== 0) return priorityDiff;
            return new Date(b.createdAt || b.startAt || 0) - new Date(a.createdAt || a.startAt || 0);
        });
    }, [items]);
    const totalPages = Math.max(1, Math.ceil(sortedItems.length / pageSize));
    const pageItems = sortedItems.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    if (id) {
        return (
            <div className="content-page p-3 p-md-4">
                <Button variant="secondary" className="mb-3" onClick={() => navigate("/pengumuman")}>
                    <FaArrowLeft className="me-2" /> Kembali ke Pusat Pengumuman
                </Button>
                {loading ? (
                    <div className="text-center py-5"><Spinner animation="border" /><div className="mt-2">Memuat pengumuman...</div></div>
                ) : error ? (
                    <Alert variant="warning">{error}</Alert>
                ) : detail ? (
                    <div className="card border-0 shadow-sm">
                        {detail.imageUrl && (
                            <img
                                src={detail.imageUrl}
                                alt=""
                                className="card-img-top"
                                style={{ maxHeight: 360, objectFit: "cover" }}
                                onError={(event) => { event.currentTarget.style.display = "none"; }}
                            />
                        )}
                        <div className="card-body p-4 p-lg-5">
                            <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                                <Badge bg={TYPE_VARIANTS[detail.type] || "secondary"}>
                                    {TYPE_LABELS[detail.type] || detail.type || "Pengumuman"}
                                </Badge>
                                {isExpired(detail) && <Badge bg="secondary">Arsip</Badge>}
                            </div>
                            <h2 className="mb-3">{detail.title}</h2>
                            <div className="text-muted small mb-4">
                                <FaCalendarAlt className="me-2" />
                                Dipublikasikan {formatDate(detail.createdAt || detail.startAt)}
                                {detail.endAt && <span> · Berakhir {formatDate(detail.endAt)}</span>}
                            </div>
                            {detail.summary && <p className="lead">{detail.summary}</p>}
                            <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", lineHeight: 1.8 }}>
                                {detail.content}
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }

    return (
        <div className="content-page p-3 p-md-4">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                <div>
                    <h2 className="text-white fw-bold mb-2"><FaBullhorn className="me-2 text-primary" />Pusat Pengumuman</h2>
                    <p className="text-white mb-0">Informasi terbaru dan arsip pengumuman SIAPMUTU.</p>
                </div>
            </div>

            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body">
                    <Row className="g-3">
                        <Col md={8}>
                            <Form.Label htmlFor="announcement-search">Cari pengumuman</Form.Label>
                            <InputGroup>
                                <InputGroup.Text><FaSearch /></InputGroup.Text>
                                <Form.Control
                                    id="announcement-search"
                                    value={search}
                                    onChange={(event) => { setSearch(event.target.value); setCurrentPage(1); }}
                                    placeholder="Cari judul atau isi pengumuman..."
                                />
                            </InputGroup>
                        </Col>
                        <Col md={4}>
                            <Form.Label htmlFor="announcement-type">Kategori</Form.Label>
                            <Form.Select id="announcement-type" value={type} onChange={(event) => { setType(event.target.value); setCurrentPage(1); }}>
                                <option value="">Semua kategori</option>
                                <option value="FEATURE">Fitur Baru</option>
                                <option value="UPDATE">Pembaruan</option>
                                <option value="IMPORTANT">Penting</option>
                                <option value="MAINTENANCE">Pemeliharaan</option>
                            </Form.Select>
                        </Col>
                    </Row>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /><div className="mt-2">Memuat pengumuman...</div></div>
            ) : error ? (
                <Alert variant="warning">{error}</Alert>
            ) : sortedItems.length === 0 ? (
                <div className="card border-0 shadow-sm">
                    <div className="card-body text-center py-5">
                        <FaBullhorn size={32} className="text-muted mb-3" />
                        <h5>Belum ada pengumuman</h5>
                        <p className="text-muted mb-0">Pengumuman yang tersedia untuk akun Anda akan tampil di halaman ini.</p>
                    </div>
                </div>
            ) : (
                <Row className="g-3">
                    {pageItems.map((item) => (
                        <Col xs={12} key={item.id}>
                            <button
                                type="button"
                                className="card border-0 shadow-sm text-start w-100"
                                style={{ cursor: "pointer", transition: "box-shadow .15s ease-in-out" }}
                                onClick={() => navigate(`/pengumuman/${item.id}`)}
                            >
                                <div className="card-body p-3 p-md-4">
                                    <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                                        <Badge bg={TYPE_VARIANTS[item.type] || "secondary"}>
                                            {TYPE_LABELS[item.type] || item.type || "Pengumuman"}
                                        </Badge>
                                        {isExpired(item) && <Badge bg="secondary">Arsip</Badge>}
                                        {item.read === false && <Badge bg="info">Belum dibaca</Badge>}
                                        {Number(item.priority) >= 80 && <Badge bg="danger">Prioritas</Badge>}
                                    </div>
                                    <h5 className="mb-2 text-dark">{item.title}</h5>
                                    {item.summary && <p className="text-muted mb-3">{item.summary}</p>}
                                    <div className="d-flex flex-wrap justify-content-between gap-2 text-muted small">
                                        <span><FaCalendarAlt className="me-2" />{formatDate(item.createdAt || item.startAt)}</span>
                                        <span>{item.endAt ? `Berakhir ${formatDate(item.endAt)}` : "Tidak ada tanggal akhir"} <span className="ms-2 text-primary">Baca detail →</span></span>
                                    </div>
                                </div>
                            </button>
                        </Col>
                    ))}
                </Row>
            )}
            {!loading && !error && sortedItems.length > pageSize && (
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-4">
                    <small className="text-muted">
                        Menampilkan {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, sortedItems.length)} dari {sortedItems.length} pengumuman
                    </small>
                    <div className="d-flex gap-2">
                        <Button variant="outline-primary" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}>
                            Sebelumnya
                        </Button>
                        <span className="align-self-center small">Halaman {currentPage} dari {totalPages}</span>
                        <Button variant="outline-primary" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}>
                            Berikutnya
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnnouncementCenter;
